import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import StudentHeader from '@/components/student/StudentHeader';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Clock, CheckCircle, XCircle, AlertCircle, Play, ArrowLeft, ArrowRight,
  Flag, Send, Timer, Award, Target, BookOpen, RotateCcw, Globe, Languages,
  Eye, EyeOff, AlertTriangle, Save, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DynamicExamPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, token } = useContext(AuthContext);
  
  // URL params
  const courseId = searchParams.get('course');
  const trainingMode = searchParams.get('mode') || 'STT';
  
  // States
  const [step, setStep] = useState('language'); // language, exam, result
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(3600); // 60 minutes
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  
  // Auth headers
  const getHeaders = () => ({
    'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // Check eligibility on mount
  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to take exam');
      navigate('/auth/login');
      return;
    }
    if (!courseId) {
      toast.error('No course selected');
      navigate('/student/dashboard');
      return;
    }
    checkEligibility();
  }, [courseId, isAuthenticated]);

  const checkEligibility = async () => {
    try {
      const res = await axios.post(
        `${API}/dynamic-exam/check-eligibility?course_id=${courseId}`,
        {},
        { headers: getHeaders() }
      );
      
      if (!res.data.eligible) {
        toast.error(res.data.reason);
        navigate('/student/dashboard');
        return;
      }
      
      // If has ongoing session, resume it
      if (res.data.has_ongoing_session) {
        setSessionId(res.data.session_id);
        toast.info('Resuming your ongoing exam...');
        resumeExam(res.data.session_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check eligibility');
    }
  };

  const resumeExam = async (sid) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/dynamic-exam/session/${sid}`,
        { headers: getHeaders() }
      );
      
      setQuestions(res.data.questions);
      setLanguage(res.data.language);
      setTimeRemaining(res.data.time_remaining_seconds);
      setCurrentIndex(res.data.current_question_index || 0);
      setAnswers(res.data.saved_answers || {});
      setTabSwitchCount(res.data.tab_switch_count || 0);
      setStep('exam');
      startTimer();
      startAutoSave();
      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume exam');
      setLoading(false);
    }
  };

  const startExam = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API}/dynamic-exam/start`,
        {
          course_id: courseId,
          training_mode: trainingMode,
          language: language
        },
        { headers: getHeaders() }
      );
      
      setSessionId(res.data.session_id);
      setCourseName(courseId.replace(/-/g, ' ').toUpperCase());
      
      // Now fetch the session with questions
      const sessionRes = await axios.get(
        `${API}/dynamic-exam/session/${res.data.session_id}`,
        { headers: getHeaders() }
      );
      
      setQuestions(sessionRes.data.questions);
      setTimeRemaining(sessionRes.data.time_remaining_seconds);
      setStep('exam');
      startTimer();
      startAutoSave();
      setLoading(false);
      
      toast.success('Exam started! Good luck!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start exam');
      setLoading(false);
    }
  };

  // Timer
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Auto-save every 30 seconds
  const startAutoSave = () => {
    if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    
    autoSaveRef.current = setInterval(() => {
      autoSaveAnswers();
    }, 30000);
  };

  const autoSaveAnswers = async () => {
    if (!sessionId || examSubmitted) return;
    
    try {
      setAutoSaving(true);
      await axios.post(
        `${API}/dynamic-exam/auto-save`,
        {
          session_id: sessionId,
          answers: answers,
          current_question_index: currentIndex
        },
        { headers: getHeaders() }
      );
      setLastSaved(new Date());
      setAutoSaving(false);
    } catch (error) {
      setAutoSaving(false);
    }
  };

  // Tab visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && step === 'exam' && !examSubmitted) {
        reportTabSwitch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [step, examSubmitted, sessionId]);

  const reportTabSwitch = async () => {
    if (!sessionId) return;
    
    try {
      const res = await axios.post(
        `${API}/dynamic-exam/report-tab-switch?session_id=${sessionId}`,
        {},
        { headers: getHeaders() }
      );
      
      if (res.data.warning) {
        setTabSwitchCount(res.data.tab_switch_count);
        setShowWarning(true);
        toast.warning(res.data.message);
        
        setTimeout(() => setShowWarning(false), 5000);
      }
    } catch (error) {
      console.error('Failed to report tab switch');
    }
  };

  // Language switch
  const switchLanguage = async (newLang) => {
    if (!sessionId) {
      setLanguage(newLang);
      return;
    }
    
    try {
      await axios.post(
        `${API}/dynamic-exam/switch-language`,
        {
          session_id: sessionId,
          language: newLang
        },
        { headers: getHeaders() }
      );
      setLanguage(newLang);
      toast.success(`Language switched to ${newLang === 'en' ? 'English' : 'Hindi'}`);
    } catch (error) {
      toast.error('Failed to switch language');
    }
  };

  // Answer selection
  const selectAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  // Navigation
  const goToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentIndex(index);
    }
  };

  // Auto-submit on timeout
  const handleAutoSubmit = async () => {
    toast.warning('Time is up! Submitting your exam...');
    await submitExam();
  };

  // Submit exam
  const submitExam = async () => {
    if (examSubmitted) return;
    
    try {
      setLoading(true);
      clearInterval(timerRef.current);
      clearInterval(autoSaveRef.current);
      
      const res = await axios.post(
        `${API}/dynamic-exam/submit`,
        {
          session_id: sessionId,
          answers: answers
        },
        { headers: getHeaders() }
      );
      
      setExamSubmitted(true);
      setResult(res.data);
      setStep('result');
      setLoading(false);
      
      if (res.data.result === 'pass') {
        toast.success('Congratulations! You passed the exam!');
        
        // Auto-generate premium certificate
        try {
          const certRes = await axios.post(`${API}/certificate/generate?exam_session_id=${res.data.session_id}`);
          if (certRes.data.certificate) {
            toast.success('Your certificate has been generated!', {
              action: {
                label: 'View Certificate',
                onClick: () => navigate(`/certificate/${certRes.data.certificate.id}`)
              }
            });
          }
        } catch (certError) {
          console.log('Certificate generation pending or already exists:', certError);
        }
      } else {
        toast.error('You did not pass. Better luck next time!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
      setLoading(false);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get question text based on language
  const getQuestionText = (q) => language === 'hi' ? (q.question_hi || q.question_en) : q.question_en;
  const getOptionText = (q, opt) => {
    const key = `option_${opt.toLowerCase()}_${language}`;
    const fallback = `option_${opt.toLowerCase()}_en`;
    return q[key] || q[fallback] || '';
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, []);

  // Render Language Selection
  if (step === 'language') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <StudentHeader />
        
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Languages className="h-10 w-10 text-blue-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Select Exam Language</h1>
              <p className="text-slate-600">परीक्षा भाषा चुनें</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-8">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Course / कोर्स:</span>
                <span className="font-bold text-slate-800">{courseId?.replace(/-/g, ' ').toUpperCase()}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-600">Mode / मोड:</span>
                <span className={`font-bold ${trainingMode === 'STT' ? 'text-blue-600' : 'text-purple-600'}`}>
                  {trainingMode === 'STT' ? 'STT - Full Training' : 'RPL - Assessment Only'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-600">Duration / अवधि:</span>
                <span className="font-bold text-slate-800">60 Minutes / 60 मिनट</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-600">Questions / प्रश्न:</span>
                <span className="font-bold text-slate-800">45 Questions / 45 प्रश्न</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-600">Passing / उत्तीर्ण:</span>
                <span className="font-bold text-green-600">60% (27 out of 45)</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => setLanguage('en')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  language === 'en'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-blue-300'
                }`}
              >
                <Globe className={`h-8 w-8 mx-auto mb-2 ${language === 'en' ? 'text-blue-600' : 'text-slate-400'}`} />
                <h3 className="text-xl font-bold text-slate-800">English</h3>
                <p className="text-sm text-slate-500">अंग्रेजी</p>
                {language === 'en' && (
                  <CheckCircle className="h-5 w-5 text-blue-600 mx-auto mt-2" />
                )}
              </button>

              <button
                onClick={() => setLanguage('hi')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  language === 'hi'
                    ? 'border-orange-500 bg-orange-50 shadow-lg'
                    : 'border-slate-200 hover:border-orange-300'
                }`}
              >
                <Globe className={`h-8 w-8 mx-auto mb-2 ${language === 'hi' ? 'text-orange-600' : 'text-slate-400'}`} />
                <h3 className="text-xl font-bold text-slate-800">हिंदी</h3>
                <p className="text-sm text-slate-500">Hindi</p>
                {language === 'hi' && (
                  <CheckCircle className="h-5 w-5 text-orange-600 mx-auto mt-2" />
                )}
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Important Instructions / महत्वपूर्ण निर्देश:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>You can switch language during exam / आप परीक्षा के दौरान भाषा बदल सकते हैं</li>
                    <li>Tab switching will be monitored / टैब स्विचिंग की निगरानी की जाएगी</li>
                    <li>Answers auto-save every 30 seconds / उत्तर हर 30 सेकंड में स्वतः सहेजे जाते हैं</li>
                    <li>Exam will auto-submit when time ends / समय समाप्त होने पर परीक्षा स्वतः सबमिट हो जाएगी</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              onClick={startExam}
              disabled={loading}
              className="w-full py-6 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {loading ? (
                <RefreshCw className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Play className="h-5 w-5 mr-2" />
              )}
              Start Exam / परीक्षा शुरू करें
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render Exam
  if (step === 'exam' && questions.length > 0) {
    const currentQuestion = questions[currentIndex];
    const answeredCount = Object.keys(answers).length;
    const isLastQuestion = currentIndex === questions.length - 1;
    const isFirstQuestion = currentIndex === 0;

    return (
      <div className="min-h-screen bg-slate-100">
        {/* Warning overlay */}
        {showWarning && (
          <div className="fixed inset-0 bg-red-500/20 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md text-center">
              <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-600 mb-2">Tab Switch Detected!</h2>
              <p className="text-slate-600">Warning #{tabSwitchCount}: Your exam may be invalidated.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white shadow-md sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <BookOpen className="h-6 w-6 text-blue-600" />
                <div>
                  <h1 className="font-bold text-slate-800">{courseId?.replace(/-/g, ' ').toUpperCase()}</h1>
                  <p className="text-xs text-slate-500">{trainingMode} Mode</p>
                </div>
              </div>

              {/* Timer */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-mono text-lg font-bold ${
                timeRemaining < 300 
                  ? 'bg-red-100 text-red-600 animate-pulse' 
                  : timeRemaining < 600 
                    ? 'bg-amber-100 text-amber-600'
                    : 'bg-green-100 text-green-600'
              }`}>
                <Timer className="h-5 w-5" />
                {formatTime(timeRemaining)}
              </div>

              {/* Language Switch */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => switchLanguage('en')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    language === 'en'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => switchLanguage('hi')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    language === 'hi'
                      ? 'bg-orange-600 text-white'
                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  हिं
                </button>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Progress</p>
                  <p className="font-bold text-slate-800">{answeredCount}/{questions.length}</p>
                </div>
                <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                    style={{ width: `${(answeredCount / questions.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Question Panel */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg p-6">
                {/* Question Header */}
                <div className="flex items-center justify-between mb-6">
                  <span className="bg-blue-100 text-blue-700 px-4 py-1 rounded-full font-medium">
                    Question {currentIndex + 1} of {questions.length}
                  </span>
                  <span className="text-sm text-slate-500">
                    {currentQuestion?.module}
                  </span>
                </div>

                {/* Question Text */}
                <div className="mb-8">
                  <h2 className="text-xl font-medium text-slate-800 leading-relaxed">
                    {getQuestionText(currentQuestion)}
                  </h2>
                </div>

                {/* Options */}
                <div className="space-y-3">
                  {['A', 'B', 'C', 'D'].map((opt) => {
                    const isSelected = answers[currentQuestion.id] === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => selectAnswer(currentQuestion.id, opt)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {opt}
                        </span>
                        <span className="text-slate-700 flex-1">
                          {getOptionText(currentQuestion, opt)}
                        </span>
                        {isSelected && (
                          <CheckCircle className="h-5 w-5 text-blue-600" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => goToQuestion(currentIndex - 1)}
                    disabled={isFirstQuestion}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    {autoSaving && <RefreshCw className="h-4 w-4 animate-spin" />}
                    {lastSaved && !autoSaving && (
                      <span>Saved {lastSaved.toLocaleTimeString()}</span>
                    )}
                  </div>

                  {isLastQuestion ? (
                    <Button
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to submit? You have answered ${answeredCount}/${questions.length} questions.`)) {
                          submitExam();
                        }
                      }}
                      disabled={loading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Submit Exam
                    </Button>
                  ) : (
                    <Button onClick={() => goToQuestion(currentIndex + 1)}>
                      Next
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Question Navigator */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-4 sticky top-24">
                <h3 className="font-bold text-slate-800 mb-4">Question Navigator</h3>
                
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {questions.map((q, idx) => {
                    const isAnswered = answers[q.id];
                    const isCurrent = idx === currentIndex;
                    return (
                      <button
                        key={q.id}
                        onClick={() => goToQuestion(idx)}
                        className={`w-full aspect-square rounded-lg font-medium text-sm transition-all ${
                          isCurrent
                            ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                            : isAnswered
                              ? 'bg-green-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="space-y-2 text-sm border-t pt-4">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded bg-green-500"></span>
                    <span className="text-slate-600">Answered ({answeredCount})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded bg-slate-100"></span>
                    <span className="text-slate-600">Not Answered ({questions.length - answeredCount})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded bg-blue-600"></span>
                    <span className="text-slate-600">Current</span>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={() => {
                    if (window.confirm(`Submit exam now? Answered: ${answeredCount}/${questions.length}`)) {
                      submitExam();
                    }
                  }}
                  disabled={loading}
                  className="w-full mt-4 bg-green-600 hover:bg-green-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Exam
                </Button>

                {tabSwitchCount > 0 && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-600">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      Tab switches: {tabSwitchCount}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Result
  if (step === 'result' && result) {
    const passed = result.result === 'pass';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <StudentHeader />
        
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
              passed ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {passed ? (
                <Award className="h-12 w-12 text-green-600" />
              ) : (
                <XCircle className="h-12 w-12 text-red-600" />
              )}
            </div>

            <h1 className={`text-3xl font-bold mb-2 ${passed ? 'text-green-600' : 'text-red-600'}`}>
              {passed ? 'Congratulations!' : 'Better Luck Next Time'}
            </h1>
            <p className="text-slate-600 mb-8">
              {passed 
                ? 'You have successfully passed the examination!'
                : 'You did not meet the passing criteria.'
              }
            </p>

            {/* Score Card */}
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className="text-sm text-slate-500">Total Questions</p>
                  <p className="text-2xl font-bold text-slate-800">{result.score?.total_questions}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Attempted</p>
                  <p className="text-2xl font-bold text-slate-800">{result.score?.attempted}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Correct</p>
                  <p className="text-2xl font-bold text-green-600">{result.score?.correct}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Wrong</p>
                  <p className="text-2xl font-bold text-red-600">{result.score?.wrong}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Your Score</p>
                  <p className={`text-3xl font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>
                    {result.score?.percentage?.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Passing Score</p>
                  <p className="text-2xl font-bold text-slate-600">{result.score?.passing_percentage}%</p>
                </div>
              </div>
            </div>

            {/* Certificate Info */}
            {passed && result.certificate_id && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
                <Award className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-700 font-medium">
                  Certificate has been generated!
                </p>
                <p className="text-sm text-green-600">
                  You can download it from your dashboard.
                </p>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/student/dashboard')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              {passed && (
                <Button
                  onClick={() => navigate('/student/certificates')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Award className="h-4 w-4 mr-2" />
                  View Certificate
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="text-center">
        <RefreshCw className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Loading exam...</p>
      </div>
    </div>
  );
};

export default DynamicExamPage;
